import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Bike, Package, Users, ChevronRight, AlertTriangle } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { getDriverStatus } from '../../services/api/trips-ui';
import { useAuth } from '../USER/Auth/AuthContext';
import { useGlobalGPS } from '../GPS/GPSContext';
import {
    acceptServiceOffer, acceptService, getDriverVehicles,
    assignVehicleToService, getCompatibleServicesWithDiagnostics, getDriverLocation,
    getDriverServicePreferences, getDriverActiveVehicleTypes
} from '../DELIVERY/services/deliveryServices';
import { driverCounterOffer } from '../../services/api/trips';
import { performServiceAccept } from '../../services/serviceAcceptFlow';
import { calcularDistancia, calcularTarifa, estimarTiempo, getActiveTariffConfig, DEFAULT_CONFIG } from '../DELIVERY/utils/tarifaCalculator';
import { DEFAULT_SEARCH_RADIUS_KM, SERVICE_STATUSES, SERVICE_TYPES, DRIVER_STATUSES } from '../../constants';
import Swal from 'sweetalert2';
import { createRoot } from 'react-dom/client';
import { useNavigate } from 'react-router-dom';
import VehiclePickerContent from './views/components/VehiclePickerContent';
import DriverIncomingExplainer from './views/components/DriverIncomingExplainer';
import ServiceOfferWizard from './ServiceOfferWizard';

export const getServiceFareSummary = (service, tariffConfig) => {
    const distKm = service._distance || service.distance || 0;
    const tarifa = calcularTarifa(
        distKm,
        0,
        estimarTiempo(distKm),
        tariffConfig,
        service.vehicle_type,
        service.payment_method
    );
    const commissionPct = tariffConfig.platform_commission;
    const priceTotal = service.offered_price || service.price;
    const rate = tarifa.total > 0 ? tarifa.commission / tarifa.total : (commissionPct / 100);
    const netTotal = service.offered_price
        ? parseFloat((service.offered_price * (1 - rate)).toFixed(2))
        : tarifa.driverPayout;
    return { tarifa, commissionPct, priceTotal, netTotal, distKm };
};

const UNAVAILABLE_MARKERS = ['not available', 'no disponible', 'ya no está disponible'];

const isServiceUnavailable = (message = '') => {
    const normalized = String(message).toLowerCase();
    return UNAVAILABLE_MARKERS.some(marker => normalized.includes(marker));
};

const DriverIncomingRequests = () => {
    const navigate = useNavigate();
    const { currentUser, isDriver } = useAuth();
    const { lastLocation, gpsOnline, setGpsOnline } = useGlobalGPS();
    const [incomingServices, setIncomingServices] = useState([]);
    const [rejectedServices, setRejectedServices] = useState(new Set());
    const [tariffConfig, setTariffConfig] = useState(DEFAULT_CONFIG);
    const [, setPickerOpen] = useState(false);
    const [offerService, setOfferService] = useState(null);
    const [visibilityDiagnostics, setVisibilityDiagnostics] = useState(null);
    const [driverStatus, setDriverStatus] = useState(null);
    const [visibilityError, setVisibilityError] = useState(false);

    useEffect(() => {
        let mounted = true;
        getActiveTariffConfig()
            .then(cfg => { if (mounted) setTariffConfig(cfg || DEFAULT_CONFIG); })
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    const fetchCompatible = useCallback(async () => {
        if (!currentUser || !isDriver) return;

        const { data: driverData, error: driverErr } = await getDriverStatus(currentUser.id);

        if (driverErr || !driverData) return;

        setDriverStatus(driverData.status || null);

        if (driverData.status !== DRIVER_STATUSES.ACTIVE) return;

        try {
            const { services, diagnostics } = await getCompatibleServicesWithDiagnostics(
                currentUser.id,
                DEFAULT_SEARCH_RADIUS_KM,
                lastLocation
            );
            setIncomingServices(services || []);
            setVisibilityDiagnostics(diagnostics || null);
            setVisibilityError(false);
        } catch (err) {
            console.error('Error fetching compatible services:', err);
            setVisibilityError(true);
        }
    }, [currentUser, isDriver, lastLocation]);

    useEffect(() => {
        fetchCompatible();
    }, [fetchCompatible, gpsOnline]);

    useEffect(() => {
        if (!currentUser || !isDriver) return;

        const subscription = supabase
            .channel('driver-incoming-services')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'services', filter: 'status=eq.searching'             }, async payload => {
                const { data: driverData } = await getDriverStatus(currentUser.id);

                if (driverData?.status !== DRIVER_STATUSES.ACTIVE) return;

                let location = await getDriverLocation(currentUser.id).catch(() => null);
                if (
                    (!location?.lat || !location?.lng || (Number(location.lat) === 0 && Number(location.lng) === 0)) &&
                    lastLocation?.latitude &&
                    lastLocation?.longitude
                ) {
                    location = { lat: lastLocation.latitude, lng: lastLocation.longitude };
                }
                if (!location?.lat || !location?.lng || (Number(location.lat) === 0 && Number(location.lng) === 0)) return;

                const svc = payload.new;
                const pickupLat = svc.pickup?.lat;
                const pickupLng = svc.pickup?.lng;
                if (!pickupLat || !pickupLng) return;

                const distKm = calcularDistancia(location.lat, location.lng, pickupLat, pickupLng);
                if (distKm > DEFAULT_SEARCH_RADIUS_KM) return;

                const activeVehicles = await getDriverActiveVehicleTypes(currentUser.id).catch(() => []);
                if (activeVehicles.length > 0 && svc.vehicle_type && !activeVehicles.includes(svc.vehicle_type)) return;

                // Verificar preferencias de categoría del conductor
                if (svc.type) {
                    const prefs = await getDriverServicePreferences(currentUser.id).catch(() => []);
                    const disabledSlugs = new Set(prefs.filter(p => !p.is_enabled).map(p => p.slug));
                    if (disabledSlugs.has(svc.type)) return;
                }

                const svcKey = svc.id || svc.service_id;
                setIncomingServices(prev => {
                    if (prev.some(s => (s.id || s.service_id) === svcKey)) return prev;
                    return [{ ...svc, _distance: parseFloat(distKm.toFixed(2)) }, ...prev];
                });

                Swal.fire({
                    toast: true,
                    position: 'top',
                    icon: 'info',
                    title: '¡Nuevo servicio disponible!',
                    text: `A ${distKm.toFixed(1)} km de ti`,
                    showConfirmButton: false,
                    timer: 3000,
                    background: '#1F2937',
                    color: 'var(--bry-text-main)',
                    iconColor: 'var(--bry-accent)'
                });

                fetchCompatible();
            })
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'services' }, payload => {
                if (payload.new.status !== SERVICE_STATUSES.SEARCHING) {
                    const updatedKey = payload.new.id || payload.new.service_id;
                    setIncomingServices(prev => prev.filter(s => (s.id || s.service_id) !== updatedKey));
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
        };
    }, [currentUser, isDriver, lastLocation, fetchCompatible]);

    const visibleServices = incomingServices
        .filter(s => !rejectedServices.has(s.id || s.service_id))
        .sort((a, b) => (a._distance || 0) - (b._distance || 0));

    if (!isDriver) return null;

    const removeIncoming = (serviceId) => {
        setIncomingServices(prev => prev.filter(s => (s.id || s.service_id) !== serviceId));
    };

    const showUnavailable = (message) => {
        Swal.fire({
            title: 'No disponible',
            text: message || 'El servicio ya no está disponible.',
            icon: 'warning',
            background: '#1F2937',
            color: 'var(--bry-text-main)',
            confirmButtonColor: 'var(--bry-accent)'
        });
    };

    const showAcceptSuccess = (serviceId, vehicleWarning = null) => {
        Swal.fire({
            icon: vehicleWarning ? 'warning' : 'success',
            title: vehicleWarning ? 'Servicio Aceptado (revisa tu vehículo)' : 'Servicio Aceptado',
            html: vehicleWarning
                ? `Servicio aceptado. ${vehicleWarning}`
                : 'Servicio aceptado. Dirígete al punto de recogida.',
            confirmButtonColor: 'var(--bry-accent)',
            background: '#1F2937',
            color: 'var(--bry-text-main)'
        });
        removeIncoming(serviceId);
        window.dispatchEvent(new CustomEvent('bryemma-service-updated', { detail: { serviceId } }));
    };

    const performAccept = async (service, vehicle) => performServiceAccept(service, vehicle, {
        accept: acceptService,
        acceptOffer: acceptServiceOffer,
        assignVehicle: assignVehicleToService,
        driverUid: currentUser.id
    });

    const openVehiclePicker = async (service, vehicles, fare) => {
        const container = document.createElement('div');
        const root = createRoot(container);
        let selectedVehicle = null;
        let unavailableMessage = null;
        let vehicleWarning = null;

        const renderPicker = (errorMessage) => {
            root.render(
                <VehiclePickerContent
                    vehicles={vehicles}
                    onSelect={(vehicle) => {
                        selectedVehicle = vehicle;
                        const confirmButton = Swal.getConfirmButton();
                        if (confirmButton) {
                            confirmButton.disabled = false;
                            confirmButton.removeAttribute('aria-disabled');
                        }
                    }}
                    onOpenProfile={() => {
                        Swal.close();
                        navigate('/perfil');
                    }}
                    error={errorMessage}
                    pickupAddress={service.pickup?.address || 'Ubicación GPS'}
                    netTotal={fare.netTotal}
                />
            );
        };

        const result = await Swal.fire({
            title: 'Selecciona tu vehiculo',
            html: container,
            showConfirmButton: true,
            confirmButtonText: 'Aceptar servicio',
            confirmButtonColor: 'var(--bry-accent)',
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
            cancelButtonColor: '#374151',
            background: 'var(--bry-bg-card, #111827)',
            color: 'var(--bry-text-primary, #F9FAFB)',
            allowOutsideClick: false,
            showLoaderOnConfirm: true,
            didOpen: () => {
                renderPicker(null);
                const confirmButton = Swal.getConfirmButton();
                if (confirmButton) {
                    confirmButton.disabled = true;
                    confirmButton.setAttribute('aria-disabled', 'true');
                }
            },
            preConfirm: async () => {
                if (!selectedVehicle) {
                    Swal.showValidationMessage('Selecciona un vehiculo para continuar');
                    return false;
                }
                try {
                    const acceptResult = await performAccept(service, selectedVehicle);
                    if (!acceptResult.ok) {
                        if (isServiceUnavailable(acceptResult.message)) {
                            unavailableMessage = acceptResult.message;
                            setTimeout(() => Swal.close(), 0);
                            return false;
                        }
                        renderPicker(acceptResult.message);
                        return false;
                    }
                    vehicleWarning = acceptResult.vehicleWarning || null;
                    return true;
                } catch (error) {
                    renderPicker(error.message || 'No se pudo aceptar el servicio. Intentalo de nuevo.');
                    return false;
                }
            },
            didDestroy: () => {
                root.unmount();
            }
        });

        return {
            accepted: Boolean(result && result.value),
            unavailableMessage,
            vehicleWarning
        };
    };

    const handleAccept = async (service) => {
        try {
            const canonicalServiceId = service.id || service.service_id;
            // Regla de Negocio: Saldo prepagado obligatorio para prestar servicios
            const { getDriverWallet, getMinWalletBalance } = await import('../DB/supabaseServices');
            const [driverWallet, minBalance] = await Promise.all([
                getDriverWallet(currentUser.id).catch(() => null),
                getMinWalletBalance().catch(() => 0)
            ]);

            const currentBalance = parseFloat(driverWallet?.balance || 0);
            const requiredMin = parseFloat(minBalance || 0);

            if (currentBalance < requiredMin) {
                Swal.fire({
                    title: 'Saldo Insuficiente',
                    text: `Tu saldo en billetera ($${currentBalance.toFixed(2)}) es insuficiente para prestar servicios. Debes tener un saldo prepagado mínimo de $${requiredMin.toFixed(2)} para laborar. Por favor contacta a administración para recargar.`,
                    icon: 'warning',
                    background: '#1F2937',
                    color: 'var(--bry-text-main)',
                    confirmButtonColor: 'var(--bry-accent)'
                });
                return;
            }

            const vehicles = await getDriverVehicles(currentUser.id);
            if (vehicles.length === 0) {
                Swal.fire({
                    title: 'Sin vehículo',
                    text: 'Debes registrar al menos un vehículo en tu perfil antes de aceptar servicios.',
                    icon: 'warning',
                    background: '#1F2937',
                    color: 'var(--bry-text-main)',
                    confirmButtonColor: 'var(--bry-accent)'
                });
                return;
            }

            if (vehicles.length === 1) {
                const singleResult = await performAccept(service, vehicles[0]);
                if (!singleResult.ok) {
                    showUnavailable(singleResult.message);
                    removeIncoming(canonicalServiceId);
                    return;
                }
                showAcceptSuccess(canonicalServiceId, singleResult.vehicleWarning);
                return;
            }

            setPickerOpen(true);
            let outcome;
            try {
                const fare = getServiceFareSummary(service, tariffConfig);
                outcome = await openVehiclePicker(service, vehicles, fare);
            } finally {
                setPickerOpen(false);
            }

            if (outcome.unavailableMessage) {
                showUnavailable(outcome.unavailableMessage);
                removeIncoming(canonicalServiceId);
                return;
            }

            if (!outcome.accepted) return;

            showAcceptSuccess(canonicalServiceId, outcome.vehicleWarning);
            return;
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: 'El servicio ya no está disponible o hubo un error.',
                icon: 'error',
                background: '#1F2937',
                color: 'var(--bry-text-main)',
                confirmButtonColor: '#EF4444'
            });
        }
    };

    const handleReject = (serviceId) => {
        // Solo lo ocultamos para este driver localmente
        setRejectedServices(prev => {
            const newSet = new Set(prev);
            newSet.add(serviceId);
            return newSet;
        });
    };

    const handleCounterOffer = async (service, presetPrice = null) => {
        let newPrice = presetPrice;
        if (newPrice) {
            const numericPrice = parseFloat(newPrice);
            if (!Number.isFinite(numericPrice) || numericPrice < service.price * 0.5 || numericPrice > service.price * 2) {
                return;
            }
        } else {
            const { value } = await Swal.fire({
            title: 'Contraoferta',
            html: `
                <div style="text-align:left">
                    <p style="color:#9CA3AF;margin-bottom:8px">Precio original: <b style="color:#10B981">$${service.price}</b></p>
                    <p style="color:#F59E0B;font-size:12px;margin-bottom:12px">Importante: Solo puedes hacer una contraoferta. Si el pasajero la rechaza, el servicio se cancela.</p>
                    <p style="color:#9CA3AF;font-size:11px">Rango permitido: $${(service.price * 0.5).toFixed(2)} - $${(service.price * 2).toFixed(2)}</p>
                </div>
            `,
            input: 'number',
            inputLabel: 'Tu contraoferta (USD)',
            inputPlaceholder: `$${service.price}`,
            inputAttributes: {
                min: (service.price * 0.5).toFixed(2),
                max: (service.price * 2).toFixed(2),
                step: '0.50'
            },
            inputValidator: (value) => {
                if (!value) return 'Ingresa un precio';
                const num = parseFloat(value);
                if (isNaN(num)) return 'Precio invalido';
                if (num < service.price * 0.5) return `Minimo $${(service.price * 0.5).toFixed(2)}`;
                if (num > service.price * 2) return `Maximo $${(service.price * 2).toFixed(2)}`;
            },
            showCancelButton: true,
            confirmButtonColor: '#10B981',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Enviar contraoferta',
            cancelButtonText: 'Cancelar',
            background: '#1F2937',
            color: '#F9FAFB'
            });
            newPrice = value;
        }

        if (!newPrice) return;

        try {
            Swal.fire({
                title: 'Enviando...',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
                background: '#1F2937',
                color: '#F9FAFB'
            });

            const canonicalServiceId = service.id || service.service_id;
            const result = await driverCounterOffer(canonicalServiceId, currentUser.id, parseFloat(newPrice));

            if (result?.success === false) {
                Swal.fire({
                    title: 'No enviado',
                    text: result.error || 'No se pudo enviar la contraoferta',
                    icon: 'warning',
                    background: '#1F2937',
                    color: '#F9FAFB',
                    confirmButtonColor: '#F59E0B'
                });
                return;
            }

            Swal.fire({
                icon: 'success',
                title: 'Contraoferta enviada',
                html: `Ofreciste <b>$${newPrice}</b>. Esperando respuesta del pasajero...`,
                confirmButtonColor: '#10B981',
                background: '#1F2937',
                color: '#F9FAFB'
            });
            setIncomingServices(prev => prev.filter(s => (s.id || s.service_id) !== canonicalServiceId));
            window.dispatchEvent(new CustomEvent('bryemma-service-updated', { detail: { serviceId: canonicalServiceId } }));
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo enviar la contraoferta',
                icon: 'error',
                background: '#1F2937',
                color: '#F9FAFB',
                confirmButtonColor: '#EF4444'
            });
        }
    };

    const isPendingDriver = Boolean(driverStatus) && driverStatus !== DRIVER_STATUSES.ACTIVE;

    return (
        <div className="px-0 py-1 mt-2">
            <DriverIncomingExplainer
                diagnostics={visibilityDiagnostics}
                gpsOnline={gpsOnline}
                driverStatus={driverStatus}
                onEnableGps={typeof setGpsOnline === 'function' ? () => setGpsOnline(true) : null}
                onRegisterVehicle={() => navigate('/perfil')}
            />

            {visibilityError && (
                <div className="bry-explainer bry-explainer--warning mb-3" role="alert">
                    <AlertTriangle size={18} aria-hidden="true" />
                    <div className="bry-explainer__body">
                        <h3 className="bry-explainer__title">No pudimos comprobar las solicitudes</h3>
                        <p className="bry-explainer__text">
                            Revisa tu conexión e inténtalo de nuevo.
                        </p>
                        <button
                            type="button"
                            className="bry-explainer__action"
                            onClick={() => fetchCompatible()}
                        >
                            Reintentar
                        </button>
                    </div>
                </div>
            )}

            {isPendingDriver ? null : visibleServices.length === 0 ? (
                visibilityError ? null : (
                <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 text-center">
                    <h5 className="font-black text-lg text-white m-0 mb-2">Solicitudes Cercanas</h5>
                    <p className="text-sm text-gray-400 m-0">
                        No hay solicitudes disponibles por ahora.
                    </p>
                </div>
                )
            ) : (
                <>
            <div className="flex items-center justify-between mb-3">
                <h5 className="font-black text-lg text-white flex items-center gap-2 m-0">
                    <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--bry-accent)] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--bry-accent)]"></span>
                    </span>
                    Solicitudes Cercanas 
                    <span className="bg-gray-800 text-[var(--bry-accent)] text-sm px-2.5 py-0.5 rounded-full border border-gray-700 font-bold">{visibleServices.length}</span>
                </h5>
            </div>
            
            <div className="space-y-3">
                {visibleServices.map(service => {
                    const fare = getServiceFareSummary(service, tariffConfig);
                    const svcKey = service.id || service.service_id;
                    return (
                    <div
                        key={svcKey}
                        className="w-full rounded-xl border border-gray-800 bg-gray-900 p-3 transition-colors hover:border-[rgba(14,171,176,0.5)]"
                    >
                        <button
                            type="button"
                            onClick={() => setOfferService(service)}
                            className="w-full text-left bg-transparent border-0 p-0 cursor-pointer"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex min-w-0 items-center gap-1.5">
                                    <span className="inline-flex items-center gap-1 rounded-md bg-[rgba(14,171,176,0.14)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--bry-accent)]">
                                        {service.type === SERVICE_TYPES.DELIVERY ? <Package size={12} /> : <Bike size={12} />}
                                        {service.type === SERVICE_TYPES.DELIVERY ? 'Entrega' : 'Viaje'}
                                    </span>
                                    {service.offered_price && (
                                        <span className="inline-flex items-center rounded-md bg-yellow-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-yellow-400">
                                            Oferta
                                        </span>
                                    )}
                                    {service.passenger_count > 1 && (
                                        <span className="inline-flex items-center gap-1 rounded-md bg-gray-800 px-2 py-0.5 text-[10px] font-bold text-gray-300">
                                            <Users size={11} /> {service.passenger_count}
                                        </span>
                                    )}
                                </div>
                                <span className="whitespace-nowrap text-[10px] font-bold text-gray-400">{fare.distKm} km</span>
                            </div>

                            <div className="mt-2 flex items-center gap-2">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <MapPin size={12} className="shrink-0 text-[var(--bry-accent)]" />
                                        <span className="truncate text-xs font-semibold text-gray-100">{service.pickup?.address || 'Ubicación GPS'}</span>
                                    </div>
                                    <div className="mt-1 flex items-center gap-1.5">
                                        <MapPin size={12} className="shrink-0 text-gray-500" />
                                        <span className="truncate text-xs font-semibold text-gray-400">{service.dropoff?.address || 'Destino en Mapa'}</span>
                                    </div>
                                </div>
                                <div className="shrink-0 text-right">
                                    <span className="block text-[9px] font-bold uppercase tracking-wider text-gray-400">Recibes</span>
                                    <span data-testid="fare-net" className="block text-base font-black leading-tight text-[var(--bry-accent)] tabular-nums">${fare.netTotal.toFixed(2)}</span>
                                </div>
                                <ChevronRight size={16} className="shrink-0 text-gray-500" />
                            </div>
                        </button>

                        {/* Botones de acción directa siempre visibles en cada solicitud */}
                        <div className="mt-3 pt-2.5 border-t border-gray-800 flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => handleReject(svcKey)}
                                style={{
                                    minHeight: 40,
                                    backgroundColor: 'rgba(239, 68, 68, 0.14)',
                                    borderColor: 'rgba(239, 68, 68, 0.4)',
                                    color: '#FCA5A5',
                                }}
                                className="px-3 rounded-lg border text-xs font-extrabold transition-colors hover:bg-red-500/25"
                            >
                                Rechazar
                            </button>
                            <button
                                type="button"
                                onClick={() => handleCounterOffer(service)}
                                style={{
                                    minHeight: 40,
                                    backgroundColor: 'rgba(245, 158, 11, 0.12)',
                                    borderColor: 'rgba(245, 158, 11, 0.4)',
                                    color: '#FCD34D',
                                }}
                                className="px-3 rounded-lg border text-xs font-extrabold transition-colors hover:bg-amber-500/20"
                            >
                                Contraoferta
                            </button>
                            <button
                                type="button"
                                onClick={() => handleAccept(service)}
                                style={{
                                    minHeight: 40,
                                    backgroundColor: '#0EABB0',
                                    color: '#FFFFFF',
                                }}
                                className="flex-1 px-3 rounded-lg border-0 text-xs font-black shadow-sm transition-opacity hover:opacity-95"
                            >
                                Aceptar (${fare.netTotal.toFixed(2)})
                            </button>
                        </div>
                    </div>
                );
                })}
            </div>
                </>
            )}

            {offerService && (() => {
                const fare = getServiceFareSummary(offerService, tariffConfig);
                return (
                    <ServiceOfferWizard
                        service={offerService}
                        fare={fare}
                        onClose={() => setOfferService(null)}
                        onAccept={(service) => {
                            setOfferService(null);
                            handleAccept(service);
                        }}
                        onReject={(serviceId) => {
                            setOfferService(null);
                            handleReject(serviceId);
                        }}
                        onCounterOffer={(service, price) => {
                            setOfferService(null);
                            handleCounterOffer(service, price);
                        }}
                    />
                );
            })()}
        </div>
    );
};

export default DriverIncomingRequests;
